<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import JobModel from '@models/Job';
import ScheduledServiceTimes from '@views/jobs/scheduledServiceTimes/scheduled-service-times';
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import FileDownloadLink from '@components/FileDownloadLink';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.jobs'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    ScheduledServiceTimes,
    FileDownloadLink,
    HeaderComponent,
  },
  props: {
    locations: {
      required: true,
      type: Array,
      default: null,
    },
    areas: {
      required: true,
      type: Array,
      default: null,
    },
    jobStatuses: {
      required: true,
      type: Array,
      default: null,
    },
    pageTitle: {
      required: true,
      default: '',
      type: String,
    },
  },
  data() {
    return {
      sortBy: 'start_date',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'invoice_number', label: this.$t('table_labels.invoice_number'), sortable: false },
        { key: 'start_date', label: this.$t('table_labels.start_date'), sortable: true, formatter: this.timeFormatter },
        { key: 'vip_name', label: this.$t('table_labels.vip'), sortable: true },
        { key: 'company_name', label: this.$t('table_labels.company'), sortable: true },
        { key: 'contact_name', label: this.$t('table_labels.contact'), sortable: true },
        { key: 'organizer', label: this.$t('table_labels.organizer'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
        status: 2,
      },
      attachmentTableFields: [
        {
          key: 'name',
          label: '',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          this.$refs.asyncTable.refresh();
        });
      },
      immediate: false,
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.$refs.asyncTable.refresh();
      }
    },
    goToEditJob(jobId) {
      const routeData = this.$router.resolve({ name: 'jobs.edit', params: { id: jobId } });
      window.open(routeData.href, '_blank');
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('dd.LL.yyyy') : null;
    },
    itemsProvider(ctx) {
      return JobModel.orderBy(this.sortDir + ctx.sortBy)
        .params({
          status: this.filters.status,
        })
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.job') }))) {
        this.$wait.start('job processing');
        const job = new JobModel({ id });
        await job.delete();
        this.$wait.end('job processing');
        this.$refs.asyncTable.refresh();
      }
    },
    async duplicate(id) {
      if (confirm(this.$t('alerts.duplicate_entity', { entity: this.$t('entities.job') }))) {
        this.$wait.start('job processing');
        this.$http
          .post(`admin/jobs/${id}/duplicate`)
          .then((response) => {
            this.$wait.end('job processing');
          })
          .catch(() => this.$wait.end('job processing'));
        this.$refs.asyncTable.refresh();
      }
    },
    exportPdf(id) {
      this.$wait.start(`downloading-job-${id}`);
      this.$http
        .get(`admin/jobs/${id}/export`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading-job-${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `Job #${id}`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-job-${id}`));
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    updateScheduledServiceTime(scheduledServiceTimes, item, key) {
      this.$set(scheduledServiceTimes, key, item);
    },
    onStatusChange(jobStatusId) {
      if (this.filters.status === jobStatusId) {
        this.$refs.asyncTable.refresh();
      } else {
        this.filters.status = jobStatusId;
      }
    },
  },
};
</script>
<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="pageTitle" :button-text="$t('titles.create_job')" @button-pressed="$router.push({ name: 'jobs.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="4" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [
                    $t('search.entities.id'),
                    $t('search.entities.vip'),
                    $t('search.entities.company'),
                    $t('search.entities.contact'),
                    $t('search.entities.invoice_number'),
                    $t('search.entities.organizer'),
                  ].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('search.status') }}</span>
          </template>
        </HeaderComponent>
        <b-form-group>
          <b-form-radio-group size="sm" buttons class="w-100 d-flex" disabled-field="notEnabled">
            <b-btn
              v-for="jobStatus in jobStatuses"
              :key="jobStatus.id"
              size="sm"
              style="flex: 1;"
              :variant="`${jobStatus.id === filters.status ? 'secondary' : 'outline-secondary'} border-gray`"
              @click="onStatusChange(jobStatus.id)"
            >
              {{ jobStatus.name }}
            </b-btn>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
    </b-row>
    <div id="reports-index" class="position-relative mt-4">
      <BlockingContainer v-if="$wait.is('job processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        responsive
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
        table-class="no-min-width"
        style="overflow: inherit"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(invoice_number)="row">
          <span v-if="row.item.invoice_number" class="text-success">{{ row.item.invoice_number.join(',') }}</span>
          <span v-else class="text-success"> - </span>
        </template>
        <template v-slot:cell(vip_name)="row">
          <span v-if="row.item.vip">{{ row.item.vip.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(company_name)="row">
          <span v-if="row.item.company">{{ row.item.company.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(contact_name)="row">
          <span v-if="row.item.contact">{{ row.item.contact.first_name }} {{ row.item.contact.last_name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(organizer)="row">
          <span v-if="row.item.organizer">{{ row.item.organizer.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(actions)="row">
          <b-btn class="mr-2" size="sm" :variant="row.detailsShowing ? 'secondary' : 'outline-secondary'" @click.stop="row.toggleDetails">
            <FontAwesomeIcon :icon="['fas', 'sticky-note']" />
          </b-btn>
          <b-dropdown variant="outline-primary" dropleft no-caret size="sm">
            <template #button-content>
              <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
            </template>

            <b-dropdown-item variant="primary" @click="goToEditJob(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
              <small>&nbsp;{{ $t('actions.edit') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="success" :disabled="$wait.is(`downloading${row.item.id}-export`)" @click="exportPdf(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'file-export']" />
              <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_pdf`)" class="ml-1" icon="spinner" spin />
              <small>&nbsp;{{ $t('actions.export_pdf') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="primary" @click.stop="duplicate(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'copy']" />
              <small>&nbsp;{{ $t('actions.duplicate') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
              <small>&nbsp;{{ $t('actions.delete') }}</small>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
        <template v-slot:row-details="row">
          <div v-if="row.item.remark" class="mb-2">
            <label>{{ $t('fields.remark') }}</label>
            <b-card>
              <!-- eslint-disable-next-line -->
              <b-card-body class="p-0">{{ row.item.remark }} </b-card-body>
            </b-card>
          </div>
          <div v-if="row.item.attachments.length > 0" class="mb-2">
            <label>{{ $t('fields.attachments') }}</label>
            <div class="border">
              <b-table stacked="lg" striped hover :items="row.item.attachments" :fields="attachmentTableFields" small thead-class="d-none" :show-empty="false" class="mb-0">
                <template v-slot:cell(name)="row">
                  <FileDownloadLink :file="row.item" />
                </template>
              </b-table>
            </div>
          </div>
          <div v-if="row.item.scheduled_service_times.length" class="mb-2">
            <ScheduledServiceTimes
              :job="row.item"
              :scheduled-service-times="row.item.scheduled_service_times"
              @updated="updateScheduledServiceTime(row.item.scheduled_service_times, ...arguments)"
            />
          </div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-start align-items-left mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          <div class="text-secondary d-flex align-items-center justify-content-center mb-3 px-4">
            {{  $t('infos.showing_results', {
              start: (currentPage - 1) * perPage + 1,
              end: Math.min(currentPage * perPage, totalRows),
              total: totalRows
            }) }}
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
